// frozen.ts — Janus C2b step 0: the STATUS FREEZE. `frozenRow(row)` projects the status-free form of a finished (compiled) row so the
// PL claim prices what the row is WITHOUT its applied statuses (owner 2026-09-16: "statuses should no longer compile into the PL").
//
// Method (plans/janus-c2b-pl-is-data-plan-2026-09-16.md §3.1 as amended by §9 findings 6–10): every cell the pricer reads is resolved
// the way the row's own readers resolve it, and each family is rebuilt from what the compile left on the row:
//   • clusters WITH provenance (computed.creature.essences / senses / speeds / attacks / reach / ac.mitigation) — the status rows are
//     dropped and the cluster's own aggregation re-applied (sum, brace lock, ½-walk derivation, the jump derivation);
//   • computed.creature.modifiers — `modifier_sources` provenance when the chokepoint persisted it (status walk families), else the
//     status DEFINITION's fragments scored against the host (the raw-append families, which the chokepoint sums without a source);
//   • the bridged defense slots — a row survives unless the statuses supply that effect and no other origin is recorded;
//   • granted ability entries tagged by the fold itself (`built_from_item_type: 'status_effect'` / `source` = a status row);
//   • vitality — the stored `base_v_max`;
//   • the top-level raw-append cells — the definition's entries removed one for one (a multiset), so a reader that falls through a
//     deleted cluster entry finds nothing status-borne underneath.
// Where a cluster's aggregation cannot be reconstructed from what is on the row (a non-status factor row, a lock, a formula that
// needs a class level, a missing status definition), the result is `unsupported: 'pl-frozen:<cluster>'` — never a guessed number.
// The frozen row drops `statuses.target`, so `frozenRow(frozenRow(x)) ≡ frozenRow(x)` and `frozenRow(x) ≡ x` for a status-free row
// (tools/test/janus-pl-frozen.test.mjs pins both, plus PL(after, frozen) == PL(before) on the paired captures).
import type { ItemFieldRow } from '../compile/item-field-reader';
import { phpJsonInt } from '../compile/php-json-values';
import { isHostileAuraSelfAnchor } from '../compile/creature-status-reader';
import { compileCreatureDerivedJump } from '../compile/creature-derived-jump';
import { scoreNamedIntegerFragmentInteger } from '../compile/formula-variable-resolution';
import type { FormulaVariableMetadata, FormulaVariableReaders } from '../compile/formula-variable-resolution';
import { integerFragmentBraces } from '../compile/status-integer-contribution';
import c1Contract from '../compile/c1-contract.json';
import { plCatalogs, plCostContract } from './contract';
import type { PlReadContext, Row } from './read-context';
import { isObj, upper } from './read-context';

export interface FrozenInputs {
    row: Row;
    ctx: PlReadContext;
    /** The status ITEM's full definition (object-API payload with item_layer_types), or null when the browser does not hold it. */
    statusDefinition: (statusItemId: string) => Row | null;
}
export interface FrozenResult {
    row: Row;
    /** Applied target statuses the projection removed. */
    statuses: number;
    /** Cluster keys the projection rebuilt (empty for a status-free row). */
    touched: string[];
    /** `pl-frozen:<cluster>` when a cluster could not be rebuilt from provenance — the row is then NOT usable for a claim. */
    unsupported: string | null;
}
export interface StatusIdentity {
    rows: Row[];
    ids: Set<string>;
    itemIds: Set<string>;
}

const num = (v: unknown): number => typeof v === 'number' ? v : (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v) : 0);
const listOf = (v: unknown): Row[] => Array.isArray(v) ? v.filter(isObj) : (isObj(v) ? Object.values(v).filter(isObj) : []);
class Refuse extends Error { constructor(readonly cluster: string) { super('pl-frozen:' + cluster); } }

/** The applied TARGET statuses of a finished row (the lean stored entries: status_id / status_item_id / status_stack …). */
export function rowStatusIdentity(row: Row): StatusIdentity {
    const statuses = isObj(row.statuses) ? row.statuses : null;
    const rows = listOf(statuses?.target);
    const ids = new Set<string>(), itemIds = new Set<string>();
    for (const st of rows) {
        const id = upper(st.status_id), item = upper(st.status_item_id);
        if (id) ids.add(id);
        if (item) itemIds.add(item);
    }
    return { rows, ids, itemIds };
}

/** Is this provenance row status-borne? The compile tags status rows by kind/category `status_effect` when the lean form keeps them, and
 * ALWAYS by `href_id` = the status row id (integer chokepoint) or the status item id (the speed / sense / essence / attack clusters). */
function isStatusSource(src: unknown, id: StatusIdentity): boolean {
    if (!isObj(src)) return false;
    if (src.kind === 'status_effect' || src.category === 'status_effect') return true;
    const href = upper(src.href_id);
    return href !== '' && (id.ids.has(href) || id.itemIds.has(href));
}
const sumValues = (rows: Row[]): number => rows.reduce((s, r) => s + num(r.value), 0);
const maxBrace = (rows: Row[]): number | null => {
    let out: number | null = null;
    for (const r of rows) if (r.value_brace !== undefined && r.value_brace !== null) { const b = num(r.value_brace); out = out === null ? b : Math.max(out, b); }
    return out;
};
function dropBreakdownStatus(entry: Row): void {
    if (isObj(entry.breakdown)) { delete entry.breakdown.status_effect; if (Object.keys(entry.breakdown).length === 0) delete entry.breakdown; }
}

/** Own only containers on a written path. Held inventory, catalog and untouched clusters stay shared. */
export function plBranchWriter(source: Row): { row: () => Row; own: (...path: string[]) => Row } {
    let row = source;
    const owned = new WeakSet<Row>();
    const copy = (value: Row): Row => { const next = { ...value }; owned.add(next); return next; };
    return {
        row: () => row,
        own: (...path) => {
            if (row === source) row = copy(source);
            let node = row;
            for (const key of path) {
                const value = isObj(node[key]) ? node[key] as Row : {};
                if (!owned.has(value)) node[key] = copy(value);
                node = node[key] as Row;
            }
            return node;
        },
    };
}

export function frozenRow(inputs: FrozenInputs): FrozenResult {
    const identity = rowStatusIdentity(inputs.row);
    if (identity.rows.length === 0) return { row: inputs.row, statuses: 0, touched: [], unsupported: null };
    const writer = plBranchWriter(inputs.row);
    let row = inputs.row;
    let computed = isObj(row.computed) ? row.computed : null;
    let creature = computed && isObj(computed.creature) ? computed.creature : null;
    const own = (...path: string[]): Row => {
        const branch = writer.own(...path);
        row = writer.row();
        computed = isObj(row.computed) ? row.computed : null;
        creature = computed && isObj(computed.creature) ? computed.creature : null;
        return branch;
    };
    const ownCreature = (...path: string[]): Row => own('computed', 'creature', ...path);
    const touched = new Set<string>();
    const ctx = inputs.ctx;
    try {
        // ---- the status definitions (suppressed payloads — a hostile aura's self anchor — contributed nothing to the fold) ----------
        const defs: Array<{ status: Row; def: Row }> = [];
        for (const st of identity.rows) {
            const itemId = upper(st.status_item_id);
            if (!itemId) throw new Refuse('status-id');
            const def = inputs.statusDefinition(itemId);
            if (!def) throw new Refuse('status-def:' + itemId);
            if (isHostileAuraSelfAnchor(def as ItemFieldRow, st.status_aoe_source, st.status_caster_id, st.status_target_id, ctx.layerValue)) continue;
            if ((ctx.itemField(def as ItemFieldRow, 'item_type') ?? '') === 'creature') continue; // shapeshift shells never fold
            defs.push({ status: st, def });
        }
        const selfId = upper(ctx.itemField(row as ItemFieldRow, 'item_id') ?? (isObj(row.encounter_creatures) ? row.encounter_creatures.item_id : ''));
        const hostAuthored = (layer: string): boolean => isObj(row.item_layer_types) && Object.prototype.hasOwnProperty.call(row.item_layer_types, layer);
        const defEntries = (def: Row, layer: string): unknown[] => Object.values(ctx.layerEntries(def as ItemFieldRow, layer) ?? {});
        const defHas = (def: Row, layer: string): boolean => ctx.hasLayer(def as ItemFieldRow, layer);
        const layersOf = (def: Row): string[] => isObj(def.item_layer_types) ? Object.keys(def.item_layer_types) : [];

        // ---- essences: base+feat is capped, ability/inventory rows add AFTER the cap — a status essence row is an `ability` row ------
        const originalBody = phpJsonInt(ctx.layerValue(row as ItemFieldRow, 'body') ?? 0);
        if (creature && isObj(creature.essences)) {
            for (const [name, original] of Object.entries(creature.essences)) {
                let entry = original as Row | undefined;
                if (!isObj(entry) || !Array.isArray(entry.sources)) continue;
                const statusRows = (entry.sources as unknown[]).filter(s => isStatusSource(s, identity)) as Row[];
                if (statusRows.length === 0) continue;
                if (entry.locked === true) throw new Refuse('essences.' + name);
                for (const s of statusRows) if (s.category !== 'ability' && s.category !== 'inventory') throw new Refuse('essences.' + name);
                entry = ownCreature('essences', name);
                entry.total = num(entry.total) - sumValues(statusRows);
                entry.sources = (entry.sources as unknown[]).filter(s => !statusRows.includes(s as Row));
                if (isObj(entry.breakdown)) for (const s of statusRows) { const k = String(s.category); if (typeof entry.breakdown[k] === 'number') { const breakdown = ownCreature('essences', name, 'breakdown'); breakdown[k] = (breakdown[k] as number) - num(s.value); if (breakdown[k] === 0) delete breakdown[k]; } }
                touched.add('essences.' + name);
            }
        }
        const frozenBody = creature && isObj(creature.essences) && isObj(creature.essences.body) ? num((creature.essences.body as Row).total) : originalBody;

        // ---- senses / reach: additive clusters (an inventory winner-takes-all never involves a status row) -----------------------------
        const subtractCluster = (path: string[], key: string, label: string, deleteWhenEmpty: boolean): void => {
            const holder = path.reduce<Row>((node, name) => node[name] as Row, creature!);
            let entry = holder[key] as Row | undefined;
            if (!isObj(entry) || !Array.isArray(entry.sources)) return;
            const statusRows = (entry.sources as unknown[]).filter(s => isStatusSource(s, identity)) as Row[];
            if (statusRows.length === 0) return;
            if (statusRows.some(s => s.value_factor !== undefined || s.value_brace !== undefined) || entry.locked === true) throw new Refuse(label);
            const remaining = (entry.sources as unknown[]).filter(s => !statusRows.includes(s as Row)) as Row[];
            if (remaining.some(s => s.value_factor !== undefined)) throw new Refuse(label);
            entry = ownCreature(...path, key);
            entry.total = num(entry.total) - sumValues(statusRows);
            entry.sources = remaining;
            if (isObj(entry.breakdown) && 'status_effect' in entry.breakdown) ownCreature(...path, key, 'breakdown');
            dropBreakdownStatus(entry);
            if (deleteWhenEmpty && remaining.length === 0 && num(entry.total) === 0) delete ownCreature(...path)[key];
            touched.add(label);
        };
        if (creature && isObj(creature.senses)) for (const name of Object.keys(creature.senses)) subtractCluster(['senses'], name, 'senses.' + name, true);
        if (creature && isObj(creature.reach)) subtractCluster([], 'reach', 'reach', false);

        // ---- speeds: walking first (Σ non-status rows, a non-status brace still locks); ½-walk derivations and the jump follow --------
        if (creature && isObj(creature.speeds)) {
            let speeds = creature.speeds;
            const ownSpeed = (name: string): Row => { const entry = ownCreature('speeds', name); speeds = creature!.speeds as Row; return entry; };
            const ownSpeeds = (): Row => { speeds = ownCreature('speeds'); return speeds; };
            const rebuildAdditive = (name: string): { changed: boolean; remaining: Row[] } => {
                let entry = speeds[name] as Row;
                const label = 'speeds.' + name;
                const sources = Array.isArray(entry.sources) ? entry.sources as Row[] : [];
                const statusRows = sources.filter(s => isStatusSource(s, identity));
                if (statusRows.length === 0) return { changed: false, remaining: sources };
                const remaining = sources.filter(s => !statusRows.includes(s));
                if (remaining.some(s => s.value_factor !== undefined)) throw new Refuse(label);
                const brace = maxBrace(remaining);
                entry = ownSpeed(name);
                entry.total = Math.max(0, brace !== null ? brace : sumValues(remaining));
                entry.locked = brace !== null;
                if (remaining.length) entry.sources = remaining; else delete entry.sources;
                if (isObj(entry.breakdown) && 'status_effect' in entry.breakdown) ownCreature('speeds', name, 'breakdown');
                dropBreakdownStatus(entry);
                touched.add(label);
                return { changed: true, remaining };
            };
            const walkBefore = isObj(speeds.walking) ? num((speeds.walking as Row).total) : 0;
            if (isObj(speeds.walking)) rebuildAdditive('walking');
            const frozenWalk = isObj(speeds.walking) ? num((speeds.walking as Row).total) : 0;
            const walkChanged = frozenWalk !== walkBefore;
            // compute/speeds.php: a persisted derivative_speed_basis.speed_walking[0] snapshot caps the ½-walk basis
            const snapRaw = isObj(row.derivative_speed_basis) && Array.isArray((row.derivative_speed_basis as Row).speed_walking) ? ((row.derivative_speed_basis as Row).speed_walking as unknown[])[0] : null;
            const snap = typeof snapRaw === 'number' || (typeof snapRaw === 'string' && snapRaw !== '' && Number.isFinite(Number(snapRaw))) ? Number(snapRaw) : null;
            const walkBasis = snap !== null && snap > 0 ? Math.min(snap, frozenWalk) : frozenWalk;
            const halfWalk = walkBasis > 0 ? Math.round(walkBasis * 0.5) : 0;
            for (const name of ['swimming', 'climbing']) {
                let entry = speeds[name] as Row | undefined;
                const defaultDerives = !hostAuthored('speed_' + name);
                if (isObj(entry)) {
                    const sources = Array.isArray(entry.sources) ? entry.sources as Row[] : [];
                    const statusRows = sources.filter(s => isStatusSource(s, identity));
                    if (statusRows.length) {
                        const remaining = sources.filter(s => !statusRows.includes(s));
                        if (remaining.length) {
                            // an explicit speed plus a status: exact only when the basis did not move and the status did not lock it
                            if (walkChanged || entry.derived === true || statusRows.some(s => s.value_brace !== undefined || s.value_factor !== undefined) || remaining.some(s => s.value_factor !== undefined)) throw new Refuse('speeds.' + name);
                            // a surviving non-status brace absorbed the additive status row: the total stands
                            entry = ownSpeed(name);
                            if (maxBrace(remaining) === null && entry.locked !== true) entry.total = num(entry.total) - sumValues(statusRows);
                            if (isObj(entry.breakdown) && 'status_effect' in entry.breakdown) ownCreature('speeds', name, 'breakdown');
                            entry.sources = remaining; dropBreakdownStatus(entry);
                        } else if (defaultDerives && halfWalk > 0) {
                            ownSpeeds()[name] = { total: halfWalk, base: 0, locked: false, derived: true };
                        } else delete ownSpeeds()[name];
                        touched.add('speeds.' + name);
                    } else if (entry.derived === true && sources.length === 0 && walkChanged) {
                        if (halfWalk > 0) { if (num(entry.total) !== halfWalk) ownSpeed(name).total = halfWalk; }
                        else delete ownSpeeds()[name];
                        touched.add('speeds.' + name);
                    } else if (sources.length && walkChanged && defaultDerives) throw new Refuse('speeds.' + name); // an authored base under a moved basis: stacks-on-default may flip
                } else if (walkChanged && defaultDerives && halfWalk > 0) {
                    ownSpeeds()[name] = { total: halfWalk, base: 0, locked: false, derived: true };
                    touched.add('speeds.' + name);
                }
            }
            for (const name of Object.keys(speeds)) {
                if (['walking', 'swimming', 'climbing', 'jumping'].includes(name)) continue;
                const entry = speeds[name];
                if (!isObj(entry)) continue;
                const { changed, remaining } = rebuildAdditive(name);
                if (changed && remaining.length === 0) delete ownSpeeds()[name];
            }
            const jump = speeds.jumping;
            const bodyChanged = frozenBody !== originalBody;
            if (frozenWalk === 0) {
                if (isObj(jump) && Array.isArray(jump.sources) && (jump.sources as Row[]).some(s => s.category !== 'derived')) throw new Refuse('speeds.jumping');
                if (isObj(jump) && walkChanged) { delete ownSpeeds().jumping; touched.add('speeds.jumping'); }
            } else if (isObj(jump) && Array.isArray(jump.sources) && (jump.sources as Row[]).some(s => s.category !== 'derived')) {
                if ((jump.sources as Row[]).some(s => isStatusSource(s, identity)) || walkChanged || bodyChanged) throw new Refuse('speeds.jumping');
            } else if (!isObj(jump) || walkChanged || bodyChanged) {
                if (hostAuthored('speed_jumping')) throw new Refuse('speeds.jumping');
                ownSpeeds().jumping = compileCreatureDerivedJump({ walk: frozenWalk, body: frozenBody }) as unknown as Row;
                touched.add('speeds.jumping');
            }
        }

        // ---- attacks: per-turn Σ rows; extra = per-turn − 1; the combat echoes follow ---------------------------------------------------
        if (creature && isObj(creature.attacks) && isObj((creature.attacks as Row).per_turn)) {
            let attacks = creature.attacks as Row, perTurn = attacks.per_turn as Row;
            const sources = Array.isArray(perTurn.sources) ? perTurn.sources as Row[] : [];
            const statusRows = sources.filter(s => isStatusSource(s, identity));
            if (statusRows.length) {
                if (statusRows.some(s => s.value_brace !== undefined) || perTurn.locked === true) throw new Refuse('attacks');
                const remaining = sources.filter(s => !statusRows.includes(s));
                const extraBefore = isObj(attacks.extra_attack) ? num((attacks.extra_attack as Row).total) : 0;
                perTurn = ownCreature('attacks', 'per_turn'); attacks = creature!.attacks as Row;
                if (isObj(perTurn.breakdown) && 'status_effect' in perTurn.breakdown) ownCreature('attacks', 'per_turn', 'breakdown');
                perTurn.total = sumValues(remaining); perTurn.sources = remaining; dropBreakdownStatus(perTurn);
                const extra = num(perTurn.total) - 1;
                const combat = isObj(creature!.combat) ? ownCreature('combat') : null;
                if (extra === 0) { delete attacks.extra_attack; if (combat) delete combat.extra_attack; }
                else {
                    const extraSources = remaining.filter(s => s.category !== 'base' && s.category !== 'remainder' && s.kind !== 'host');
                    attacks.extra_attack = { total: extra, sources: extraSources };
                    if (combat) combat.extra_attack = extra;
                }
                if (combat) {
                    const hindered = (typeof combat.hindered_attack_counter === 'number' ? combat.hindered_attack_counter : 0) + extraBefore - extra;
                    if (hindered === 0) delete combat.hindered_attack_counter; else combat.hindered_attack_counter = hindered;
                }
                touched.add('attacks');
            }
        }

        // ---- damage mitigation: computed.creature.ac.mitigation carries the provenance; the scalar twin follows ---------------------------
        if (creature && isObj(creature.ac) && isObj((creature.ac as Row).mitigation)) {
            let ac = creature.ac as Row, mit = ac.mitigation as Row;
            const sources = Array.isArray(mit.sources) ? mit.sources as Row[] : [];
            const statusRows = sources.filter(s => isStatusSource(s, identity));
            if (statusRows.length) {
                if (statusRows.some(s => s.value_brace !== undefined) || mit.locked === true) throw new Refuse('damage_mitigation');
                const remaining = sources.filter(s => !statusRows.includes(s));
                const total = num(mit.total) - sumValues(statusRows);
                ac = ownCreature('ac');
                if (remaining.length === 0 && total === 0) { delete ac.mitigation; delete creature!.damage_mitigation; }
                else {
                    mit = ownCreature('ac', 'mitigation');
                    mit.total = total; mit.sources = remaining;
                    if (isObj(mit.breakdown) && 'status_effect' in mit.breakdown) ownCreature('ac', 'mitigation', 'breakdown');
                    dropBreakdownStatus(mit);
                    if (isObj(creature!.damage_mitigation)) ownCreature('damage_mitigation').total = total;
                }
                touched.add('damage_mitigation');
            }
        }

        // ---- integer modifiers: provenance when persisted, else the definition's fragments scored against the host -------------------
        if (creature && isObj(creature.modifiers)) {
            let modifiers = creature.modifiers;
            let modSources = isObj(creature.modifier_sources) ? creature.modifier_sources : null;
            const cats = plCatalogs(plCostContract);
            const metadata: FormulaVariableMetadata = { classes: [], essences: { body: cats.essences.getId('body') ?? '', mind: cats.essences.getId('mind') ?? '', soul: cats.essences.getId('soul') ?? '' } };
            const readers: FormulaVariableReaders = {
                layerValue: ctx.layerValue, layerEntries: ctx.layerEntries, itemField: ctx.itemField,
                computedValue: (r, bucket, key) => { const b = isObj(r) && isObj(r.computed) ? (r.computed as Row)[bucket] : null; const v = isObj(b) ? b[key] : null; return isObj(v) && 'total' in v ? v.total : v ?? null; },
            };
            for (const layer of Object.keys(modifiers)) {
                const total = num(modifiers[layer]);
                const rows = modSources && Array.isArray(modSources[layer]) ? modSources[layer] as Row[] : null;
                let contribution = 0;
                const statusRows = rows ? rows.filter(s => isStatusSource(s, identity)) : [];
                if (rows && rows.some(s => !isStatusSource(s, identity) && s.value_brace !== undefined && s.value_brace !== null)) continue; // an equipped {x} override absorbed the statuses
                if (statusRows.length) {
                    contribution = sumValues(statusRows); // the chokepoint's own rows: already quantity-scaled
                    const remaining = rows!.filter(s => !statusRows.includes(s));
                    modSources = ownCreature('modifier_sources');
                    if (remaining.length) modSources![layer] = remaining; else delete modSources![layer];
                } else {
                    // no status row persisted: the raw-append path (the fragment was summed into the host slot, unscaled) — score the
                    // definition's PLAIN integer fragments; a brace or any formula tag is a refusal (its inputs at compile time are unknown)
                    for (const { def } of defs) {
                        if (!defHas(def, layer)) continue;
                        const fragments = ctx.layerEntries(def as ItemFieldRow, layer);
                        if (integerFragmentBraces(fragments).length) throw new Refuse('modifiers.' + layer);
                        for (const f of Object.values(fragments)) if (typeof f === 'string' && /[\[{]/.test(f)) throw new Refuse('modifiers.' + layer);
                        contribution += Number(scoreNamedIntegerFragmentInteger(inputs.row as ItemFieldRow, fragments, layer, metadata, readers));
                    }
                    if (contribution === 0) continue;
                }
                const next = total - contribution;
                modifiers = ownCreature('modifiers');
                if (next === 0) delete modifiers[layer]; else modifiers[layer] = next;
                touched.add('modifiers.' + layer);
            }
            if (modSources && Object.keys(modSources).length === 0) delete ownCreature().modifier_sources;
            if (Object.keys(modifiers).length === 0) delete ownCreature().modifiers;
        }

        // ---- bridged defense slots: keep a row unless the statuses supply that effect and no other origin is recorded ------------------
        if (creature && isObj(creature.defenses)) {
            let defenses = creature.defenses;
            const slots: Record<string, string> = { resistance: 'damage_type_resistance', immune: 'damage_type_immunity', vulnerable: 'damage_type_vulnerability', immune_status: 'immune_status_effect',
                contesting_roll_adv_vs_status_effect: 'contesting_roll_adv_vs_status_effect', contesting_roll_dis_vs_status_effect: 'contesting_roll_dis_vs_status_effect' };
            const foreign = (id: unknown): boolean => { const u = upper(id); return u !== '' && u !== selfId && !identity.ids.has(u) && !identity.itemIds.has(u); };
            for (const [slot, layer] of Object.entries(slots)) {
                if (!Array.isArray(defenses[slot]) || hostAuthored(layer)) continue;
                const union = new Set<string>();
                for (const { def } of defs) for (const e of defEntries(def, layer)) { const u = upper(isObj(e) ? (e.damage_type ?? e.item_layer_effect ?? e[0]) : e); if (u) union.add(u); }
                if (union.size === 0) continue;
                const kept = (defenses[slot] as unknown[]).filter(r => {
                    if (!isObj(r)) return true;
                    const effect = upper(r.damage_type ?? r.item_layer_effect ?? r.effect);
                    if (!union.has(effect)) return true;
                    if (Array.isArray(r.all_sources) && (r.all_sources as Row[]).some(s => isObj(s) && foreign(s.source_item_id))) return true;
                    return foreign(r.source_item_id) && r.source_kind !== 'status_effect';
                });
                if (kept.length === (defenses[slot] as unknown[]).length) continue;
                defenses = ownCreature('defenses');
                if (kept.length) defenses[slot] = kept; else delete defenses[slot];
                touched.add('defenses.' + slot);
            }
            for (const slot of ['force_move_resistance', 'immune_to_forced_movement']) {
                if (!Array.isArray(defenses[slot])) continue;
                const rows = defenses[slot] as unknown[];
                const statusBorne = (r: unknown): boolean => isObj(r) && (r.source_kind === 'status_effect' || identity.ids.has(upper(r.source_item_id)) || identity.itemIds.has(upper(r.source_item_id)) || identity.ids.has(upper(r.status_id)));
                if (rows.some(r => isObj(r) && r.source_item_id === undefined && r.source_kind === undefined) && defs.some(({ def }) => defHas(def, slot))) throw new Refuse('defenses.' + slot);
                const kept = rows.filter(r => !statusBorne(r));
                if (kept.length === rows.length) continue;
                defenses = ownCreature('defenses');
                if (kept.length) defenses[slot] = kept; else delete defenses[slot];
                touched.add('defenses.' + slot);
            }
        }

        // ---- granted abilities: the fold tags its own entries ---------------------------------------------------------------------------
        if (creature && isObj(creature.abilities)) {
            let abilities = creature.abilities;
            const statusBorne = (e: Row): boolean => e.built_from_item_type === 'status_effect' || (typeof e.built_from === 'string' && e.built_from.startsWith('status_effect_')) || identity.ids.has(upper(e.built_from_id)) || identity.ids.has(upper(e.source));
            for (const bucket of Object.keys(abilities)) {
                const list = abilities[bucket];
                const entries = listOf(list);
                if (entries.length === 0) continue;
                const kept = entries.filter(e => !statusBorne(e));
                if (kept.length === entries.length) continue;
                abilities = ownCreature('abilities');
                if (kept.length) abilities[bucket] = kept; else delete abilities[bucket];
                touched.add('abilities.' + bucket);
            }
        }

        // ---- vitality: the stored base maximum is the status-free maximum ----------------------------------------------------------------
        if (computed && isObj(computed.vitality)) {
            let v = computed.vitality;
            const vitalityIds = new Set(((c1Contract as { vitalityStatusIds?: string[] }).vitalityStatusIds ?? []).map(upper));
            if (typeof v.base_v_max === 'number') {
                if (num(v.v_max) !== v.base_v_max) { v = own('computed', 'vitality'); v.v_max = v.base_v_max; if ('effective_v_max' in v) v.effective_v_max = v.base_v_max; touched.add('vitality'); }
            } else if ([...identity.itemIds].some(id => vitalityIds.has(id))) throw new Refuse('vitality');
        }

        // ---- top-level raw-append cells: the definition's entries removed one for one ---------------------------------------------------
        const neverAppended = new Set(['skill_roll_adv', 'skill_roll_dis', 'skill_roll_mod', 'teleport', 'change_creature_size', 'force_move_resistance', 'immune_to_forced_movement',
            'passive_ability', 'full_ability', 'ability', 'quick_ability', 'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability', 'curse', 'sanguine', 'menta', 'innate_spell_per_long_rest', 'at_will_spell']);
        for (const { def } of defs) {
            for (const layer of layersOf(def)) {
                if (neverAppended.has(layer) || layer.startsWith('speed_')) continue;
                const cell = row[layer];
                if (!Array.isArray(cell) || cell.length === 0) continue;
                const wanted = defEntries(def, layer).map(e => JSON.stringify(e));
                if (wanted.length === 0) continue;
                const next = [...cell];
                let removed = false;
                for (const w of wanted) { const i = next.findIndex(e => JSON.stringify(e) === w); if (i >= 0) { next.splice(i, 1); removed = true; } }
                if (!removed) continue;
                if (next.length) own()[layer] = next; else delete own()[layer];
                touched.add('cell.' + layer);
            }
        }

        // ---- the frozen row carries no target statuses: pricing it twice is pricing it once ----------------------------------------------
        if (isObj(row.statuses)) { const statuses = own('statuses'); delete statuses.target; if (Object.keys(statuses).length === 0) delete row.statuses; }
        if (computed && isObj(computed._cache)) delete own('computed')._cache;
        return { row, statuses: identity.rows.length, touched: [...touched].sort(), unsupported: null };
    } catch (e) {
        if (e instanceof Refuse) return { row: inputs.row, statuses: identity.rows.length, touched: [...touched].sort(), unsupported: e.message };
        throw e;
    }
}
